import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { TrendingUp, Target, ClipboardCheck, MessageSquare, RefreshCw } from "lucide-react";
import { api } from "../services/api";

export default function PerformanceDashboard() {
  const { t } = useTranslation();
  const [cycles, setCycles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("v1/performance/cycles/")
      .then((r) => setCycles(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const activeCycle = cycles.find((c) => c.status === "active");
  const totalCycles = cycles.length;
  const pendingFeedbacks = 0; // loaded separately in MyFeedback tab

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: "#0ea5e9" }} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* KPI cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <KpiCard
          icon={<RefreshCwIcon />}
          label={t("performance.dashboard.kpiActiveCycle")}
          value={activeCycle ? activeCycle.name : t("common.none")}
          sub={activeCycle ? t("performance.dashboard.kpiEvaluations", { completed: activeCycle.completed_reviews, total: activeCycle.total_reviews }) : t("performance.dashboard.kpiCreateFirst")}
          color="#0ea5e9"
        />
        <KpiCard
          icon={<Target className="h-5 w-5" />}
          label={t("performance.dashboard.kpiCyclesCreated")}
          value={totalCycles}
          sub={t("performance.dashboard.kpiSinceLaunch")}
          color="#8b5cf6"
        />
        <KpiCard
          icon={<MessageSquare className="h-5 w-5" />}
          label={t("performance.dashboard.kpiPendingFeedbacks")}
          value={pendingFeedbacks}
          sub={t("performance.dashboard.kpiFillNow")}
          color="#f59e0b"
        />
      </div>

      {/* Active cycle progress */}
      {activeCycle ? (
        <div
          className="rounded-xl p-6 space-y-4"
          style={{ backgroundColor: "var(--lm-s2)", border: "1px solid var(--lm-border)" }}
        >
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold" style={{ color: "var(--lm-ink)" }}>{activeCycle.name}</h3>
              <p className="text-sm mt-0.5" style={{ color: "var(--lm-ink-2)" }}>
                {activeCycle.start_date} → {activeCycle.end_date}
              </p>
            </div>
            <span
              className="px-2.5 py-1 rounded-full text-xs font-medium"
              style={{ backgroundColor: "rgba(14,165,233,0.12)", color: "#0ea5e9" }}
            >
              {t("performance.status.active")}
            </span>
          </div>

          {activeCycle.total_reviews > 0 && (
            <div className="space-y-1.5">
              <div className="flex justify-between text-sm" style={{ color: "var(--lm-ink-2)" }}>
                <span>{t("performance.dashboard.reviewsProgress")}</span>
                <span className="font-medium" style={{ color: "var(--lm-ink)" }}>
                  {activeCycle.completed_reviews}/{activeCycle.total_reviews}
                </span>
              </div>
              <div className="h-2 rounded-full" style={{ backgroundColor: "var(--lm-border)" }}>
                <div
                  className="h-2 rounded-full transition-all"
                  style={{
                    width: `${activeCycle.total_reviews > 0 ? (activeCycle.completed_reviews / activeCycle.total_reviews) * 100 : 0}%`,
                    backgroundColor: "#0ea5e9",
                  }}
                />
              </div>
            </div>
          )}
        </div>
      ) : (
        <div
          className="rounded-xl p-8 flex flex-col items-center gap-3 text-center"
          style={{ backgroundColor: "var(--lm-s2)", border: "1px dashed var(--lm-border)" }}
        >
          <ClipboardCheck className="h-10 w-10" style={{ color: "var(--lm-ink-3)" }} />
          <p className="font-medium" style={{ color: "var(--lm-ink)" }}>{t("performance.dashboard.noActiveCycle")}</p>
          <p className="text-sm" style={{ color: "var(--lm-ink-2)" }}>
            {t("performance.dashboard.createPromptPre")}<strong>{t("performance.tabs.cycles.label")}</strong>{t("performance.dashboard.createPromptPost")}
          </p>
        </div>
      )}

      {/* All cycles list */}
      {cycles.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-semibold" style={{ color: "var(--lm-ink)" }}>{t("performance.dashboard.allCycles")}</h4>
          {cycles.map((cycle) => (
            <div
              key={cycle.id}
              className="flex items-center justify-between px-4 py-3 rounded-lg"
              style={{ backgroundColor: "var(--lm-s2)", border: "1px solid var(--lm-border)" }}
            >
              <div className="flex items-center gap-3">
                <StatusDot status={cycle.status} />
                <div>
                  <p className="text-sm font-medium" style={{ color: "var(--lm-ink)" }}>{cycle.name}</p>
                  <p className="text-xs" style={{ color: "var(--lm-ink-2)" }}>{cycle.type}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xs" style={{ color: "var(--lm-ink-2)" }}>
                  {cycle.completed_reviews}/{cycle.total_reviews} {t("performance.dashboard.evals")}
                </span>
                <CycleStatusBadge status={cycle.status} t={t} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function KpiCard({ icon, label, value, sub, color }) {
  return (
    <div
      className="rounded-xl p-5 space-y-3"
      style={{ backgroundColor: "var(--lm-s2)", border: "1px solid var(--lm-border)" }}
    >
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium" style={{ color: "var(--lm-ink-2)" }}>{label}</span>
        <span style={{ color }}>{icon}</span>
      </div>
      <p className="text-2xl font-bold" style={{ color: "var(--lm-ink)" }}>{value}</p>
      <p className="text-xs" style={{ color: "var(--lm-ink-3)" }}>{sub}</p>
    </div>
  );
}

function RefreshCwIcon() {
  return <RefreshCw className="h-5 w-5" />;
}

function StatusDot({ status }) {
  const color = status === "active" ? "#22c55e" : status === "closed" ? "#94a3b8" : "#f59e0b";
  return <span className="h-2 w-2 rounded-full inline-block flex-shrink-0" style={{ backgroundColor: color }} />;
}

function CycleStatusBadge({ status, t }) {
  const styles = {
    draft:  { bg: "rgba(245,158,11,0.12)",  color: "#f59e0b", label: t("performance.status.draft") },
    active: { bg: "rgba(34,197,94,0.12)",   color: "#22c55e", label: t("performance.status.active") },
    closed: { bg: "rgba(148,163,184,0.12)", color: "#94a3b8", label: t("performance.status.closed") },
  };
  const s = styles[status] || styles.draft;
  return (
    <span className="px-2 py-0.5 rounded-full text-xs font-medium" style={{ backgroundColor: s.bg, color: s.color }}>
      {s.label}
    </span>
  );
}
